/**
 * HTTP API client — axios instance + interceptors.
 * All API calls go through this module.
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { ENDPOINTS } from './endpoints';
import { ApiResponse, CreateTodoRequest, UpdateTodoRequest, PaginatedResponse, SmartTodoResult } from '../types/api';
import { Todo, TodoCompletion, PluginInfo, PluginManifest, CalendarEvent, Household, Notification, Appliance, ServiceRecord, Project, ProjectDetail, ProjectDependency, ProjectTodoNode, Contact, BackgroundTask, Suggestion } from '../types/domain';
import { getActiveFlowId, startFlow, finalizeFlow, emitPoint } from '@/features/qpl-client';
import { classifyHttpError } from './http-error';
import {
  RETRY_MAX_RETRIES,
  fullJitterDelayMs,
  shouldRetryTransportError,
} from './retry';
import { calendarService } from '../contracts/calendar.routes';
import { todoService } from '../contracts/todo.routes';
import { maintenanceService } from '../contracts/maintenance.routes';
import type {
  ResearchResult,
  ResearchSource,
  ProposedTask,
  StartResearchResponse,
  ApplyResearchResponse,
} from '../contracts/maintenance';
import type {
  AppleCalendarSetupResponse,
  AppleCalendarConnectionResponse,
  CalendarConnection,
  CalendarConnectionListResponse,
  GoogleAuthInitResponse,
  GoogleCalendarListResponse,
  AppleCalendarSyncResponse,
  Subcalendar,
} from '../contracts/calendar';
// BFF auth: cookies carry identity, not bearer tokens. The session
// cookie is HttpOnly and inaccessible to JS; same-origin fetch
// attaches it automatically as long as withCredentials is set.

// ─── Mutable Client Reference ────────────────────────────
// createApiClient() updates this so tests can install MockAdapter
// on a fresh instance and have the exported functions use it.

let _client: AxiosInstance = null!;

// ─── Failure classification (QPL observability) ───────────
// Lives in ./http-error so the retry policy can classify failures
// without importing this module (which imports the policy). Re-exported
// here because callers and tests have always imported it from the client.
export type { HttpErrorKind, HttpErrorInfo } from './http-error';
export { classifyHttpError } from './http-error';

// ─── In-flight request registry (resume recovery) ─────────
// When the host suspends or the tab is frozen, requests already in
// flight are stranded on sockets that no longer exist: the browser
// won't fire their timeout until the page thaws, which is why QPL saw
// `http_call` flows ending 10 seconds — or 30 minutes — after they
// started. On resume we abort them explicitly so each one fails fast
// and the retry policy can re-issue it over a fresh connection instead
// of waiting on a dead one.
//
// Requests that carry a caller-supplied `signal` are left alone: their
// owner (e.g. a superseded search) manages that lifecycle already, and
// aborting it from here would race with the caller's own bookkeeping.
const inFlight = new Set<AbortController>();

/**
 * Abort every in-flight request this client owns. Returns how many were
 * aborted so the caller can record it (the sync coordinator annotates
 * its cycle flow with this).
 */
export function abortInFlightRequests(): number {
  const controllers = Array.from(inFlight);
  inFlight.clear();
  for (const controller of controllers) {
    try {
      controller.abort();
    } catch {
      // an already-aborted controller is not an error
    }
  }
  return controllers.length;
}

/** Key under which we stash the AbortController this client created. */
const OWNED_ABORT = '_qplOwnedAbort';

/**
 * Attach a client-owned AbortController to `config`, replacing the one
 * from a previous attempt if there is one. No-op when the caller supplied
 * its own signal — that lifecycle belongs to them.
 */
function adoptAbortController(config: any): void {
  releaseAbortController(config);
  if (typeof AbortController === 'undefined') return;
  if (config.signal) return; // caller-owned
  const controller = new AbortController();
  config.signal = controller.signal;
  config[OWNED_ABORT] = controller;
  inFlight.add(controller);
}

/**
 * Drop the client-owned controller for a settled (or about-to-be-retried)
 * request. Clearing `config.signal` matters for retries: axios-retry
 * replays the same config, and an already-aborted signal on it would
 * abort the next attempt immediately.
 */
function releaseAbortController(config: any): void {
  const owned: AbortController | undefined = config?.[OWNED_ABORT];
  if (!owned) return;
  inFlight.delete(owned);
  delete config[OWNED_ABORT];
  if (config.signal === owned.signal) delete config.signal;
}

/** 1-based attempt number for a request (axios-retry counts retries). */
function attemptNumber(config: any): number {
  const retryCount = config?.['axios-retry']?.retryCount;
  return (typeof retryCount === 'number' ? retryCount : 0) + 1;
}

// ─── Factory: Create API Client ───────────────────────────

export function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
    // BFF auth: send the chores_session cookie on every request. The
    // SPA and the API are same-origin behind nginx, so withCredentials
    // is sufficient — no CORS dance needed.
    withCredentials: true,
  });

  // ─── Request Interceptor: De-dupe /api/v1 prefix ─────────
  // Generated proto routes carry the absolute path (e.g. "/api/v1/auth/login"),
  // but baseURL is also "/api/v1" in prod (.env.production), so naive concat
  // would yield "/api/v1/api/v1/auth/login" → 404. Strip a leading prefix
  // from the request URL when baseURL already provides it. Legacy ENDPOINTS
  // callers that pass relative paths (e.g. "/auth/login") are unaffected.
  client.interceptors.request.use((config) => {
    const base = (config.baseURL || '').replace(/\/+$/, '');
    if (base && config.url && config.url.startsWith(base + '/')) {
      config.url = config.url.slice(base.length);
    }
    return config;
  });

  // ─── Request Interceptor: User timezone ──────────────────
  client.interceptors.request.use(async (config) => {
    // BFF auth: no Authorization header injection. The HttpOnly
    // chores_session cookie is attached by the browser via
    // withCredentials above; the server's sessionMiddleware unwraps it
    // into the bearer requireAuth expects. The SPA never sees a token.
    // Every request carries the browser's current IANA timezone. Server-side
    // usertime.Middleware reads this into ctx; change-detection compares it
    // against users.timezone and fires plugin hooks on mismatch.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) config.headers['X-User-Timezone'] = tz;
    } catch {
      // Intl unavailable — middleware falls back to profile TZ
    }
    // Each outbound HTTP call opens a dedicated child flow (`http_call`)
    // whose parent is the currently-active flow (nav flow set by the
    // navigation-flow manager, or the WS flow set by the WS-message
    // wrapper — never both, since saga dispatch is single-threaded and
    // only those two sources write `setActiveFlow`). The child flow's
    // UUID is sent as `X-QPL-ID` so the server-side QPLMiddleware
    // attaches its http_request_received / http_response_sent points to
    // this child, and `requireAuth` parents its `auth_check` grandchild
    // under the same id. Every HTTP call therefore appears in the
    // dashboard as its own subtree, stitched recursively by
    // parent_flow_id — no timing heuristics, no cross-saga span-id
    // collisions.
    //
    // This interceptor runs once per ATTEMPT (axios-retry re-enters the
    // whole chain), so each retry gets its own http_call flow and its own
    // X-QPL-ID. That keeps the dashboard honest: a cycle that succeeded on
    // the second try shows a FAILED attempt followed by a SUCCESS one,
    // rather than a single row that hides the retry.
    try {
      // Clear the previous attempt's stitching so a retry can't inherit a
      // finalized flow id (or send a stale X-QPL-ID if we decide not to
      // open a flow this time round).
      delete (config as any)._qplHttpCallId;
      delete (config as any)._qplStartedAt;
      if (config.headers) delete config.headers['X-QPL-ID'];
      (config as any)._qplAttempt = attemptNumber(config);

      const parentFlowId = getActiveFlowId();
      // No active gesture / nav / WS flow → don't conjure an orphan
      // http_call root. Mount-effect fetches and other out-of-saga calls
      // would otherwise flood the dashboard's root list with rows that
      // describe leaves of a missing tree.
      if (parentFlowId) {
        const shortUrl = (config.url || '').replace(/^\/api\/v1/, '') || '/';
        const handle = startFlow(
          'http_call',
          {
            method: (config.method || 'get').toUpperCase(),
            url: shortUrl,
            attempt: String((config as any)._qplAttempt),
          },
          { parent_flow_id: parentFlowId },
        );
        config.headers['X-QPL-ID'] = handle.id;
        (config as any)._qplHttpCallId = handle.id;
        (config as any)._qplStartedAt = Date.now();
      }
    } catch {
      // qpl-client unavailable — never break requests on instrumentation
    }

    // Give every request an abort handle we own, so a resume can tear
    // down requests stranded on dead sockets (see abortInFlightRequests).
    // A retry must get a FRESH controller: axios-retry replays the same
    // config object, and re-using an aborted signal would abort the new
    // attempt the instant it starts.
    adoptAbortController(config);

    return config;
  });

  // Finalize the dedicated http_call child flow opened on request. On
  // success the row's own ended_at / outcome carry timing + result and
  // no point is needed. On FAILURE we first emit a single `http_error`
  // point classifying the failure, because a bare FAILED outcome is
  // ambiguous: in the dashboard a request that 500'd, 401'd, timed out,
  // or never left the browser (offline / backend down) all look
  // identical — and that ambiguity is exactly what makes a backend-down
  // blip get misread as a broken saga. The point carries a queryable
  // shape (kind / status / code / reached_server) so failures are
  // triaged from `point[payload.kind]=network` in one query instead of
  // cross-referencing server logs. finalizeFlow's `reason` arg is only
  // honoured for CANCELED, hence the point rather than a reason string.
  //
  // `willRetry` distinguishes an attempt the transport-retry layer is
  // about to replay from a terminal failure, so a dashboard query can
  // separate "one flaky attempt, recovered" from "the cycle was lost" —
  // which is the whole point of adding retry in the first place.
  const finalizeHttpCall = (
    config: any,
    ok: boolean,
    error?: AxiosError,
    willRetry = false,
  ) => {
    try {
      const id = config?._qplHttpCallId as string | undefined;
      if (!id) return;
      if (ok) {
        finalizeFlow(id, 'SUCCESS');
        return;
      }
      const info = classifyHttpError(error);
      emitPoint(id, 'http_error', {
        kind: info.kind,
        status: info.status,
        code: info.code,
        reached_server: info.reachedServer,
        // navigator.onLine is a weak signal (true doesn't guarantee
        // reachability) but a `false` here is a strong "the browser
        // knew it was offline" marker that distinguishes a client
        // network drop from a real server-side error.
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        // Stamped at issue time by the request interceptor. Read from the
        // config rather than recomputed, because axios-retry increments
        // its retryCount BEFORE invoking onRetry — recomputing here would
        // label the failed attempt with its successor's number.
        attempt: (config?._qplAttempt as number) || 1,
        will_retry: willRetry,
      });
      // A deliberate abort is not a failure — it's us (or the caller)
      // reclaiming a request, e.g. tearing down sockets stranded by a
      // host suspension. Recording those as FAILED buried the real
      // failures under noise we caused ourselves.
      finalizeFlow(id, info.kind === 'canceled' ? 'CANCELED' : 'FAILED');
    } catch {
      // instrumentation never breaks request handling
    }
  };

  // ─── Transport retry ─────────────────────────────────────
  // Registered before the envelope translator below, deliberately:
  // response interceptors run in registration order, and the translator
  // converts an AxiosError into a plain Error. If retry sat after it,
  // there would be nothing left to classify or replay.
  //
  // Consequence: intermediate attempts never reach the response
  // interceptor, so their QPL flow is finalized in onRetry instead.
  axiosRetry(client, {
    retries: RETRY_MAX_RETRIES,
    retryCondition: shouldRetryTransportError,
    retryDelay: (retryCount) => fullJitterDelayMs(retryCount),
    // Each attempt gets the full timeout budget. With the default
    // (false), axios-retry subtracts the previous attempt's elapsed time
    // from config.timeout — and since our failure mode is *consuming the
    // entire timeout*, the remaining budget would always be negative and
    // the retry would be discarded before it was ever made.
    shouldResetTimeout: true,
    onRetry: (_retryCount, error, config) => {
      releaseAbortController(config);
      finalizeHttpCall(config, false, error as AxiosError, true);
    },
  });

  // 401 handling under BFF auth: the chores backend refreshes the AT
  // automatically via sessionMiddleware whenever the cookie's session
  // is near-expiry, so a 401 means the cookie is either absent (user
  // never signed in, or signed out from another tab) or revoked
  // (foauth dropped the RT family). Either way the SPA can't recover
  // client-side — only a new /auth/start flow can. Surface the 401
  // up so ProtectedRoute redirects to /login; QPL still records the
  // failure so we can see auth-drop storms in the dashboard.
  client.interceptors.response.use(
    (response) => {
      releaseAbortController(response.config);
      finalizeHttpCall(response.config, true);
      return response;
    },
    async (error: AxiosError) => {
      const cfg = error.config as AxiosRequestConfig | undefined;
      // Terminal failure — the retry layer above already declined to
      // replay this one (or exhausted its attempts).
      releaseAbortController(cfg);
      finalizeHttpCall(cfg, false, error);

      // For non-401 HTTP errors with an envelope, extract the envelope error message
      const responseData = error.response?.data as any;
      if (responseData?.success === false) {
        const message = responseData?.error?.message || 'Unknown API error';
        // Preserve the typed error code + HTTP status on the thrown Error
        // so callers can branch on specific codes (e.g. TODO_NOT_BLOCKABLE
        // returns 409 with code TODO_NOT_BLOCKABLE). Older callers that
        // only read .message keep working unchanged.
        const err = new Error(message) as Error & { code?: string; status?: number };
        err.code = responseData?.error?.code;
        err.status = error.response?.status;
        return Promise.reject(err);
      }

      // Non-401 errors without envelope: pass through as-is (preserve AxiosError)
      return Promise.reject(error);
    }
  );

  // Update the module-level reference so exported functions use this client
  _client = client;

  return client;
}

// ─── Default Instance ─────────────────────────────────────

const apiClient: AxiosInstance = createApiClient();

// ─── Helper: unwrap envelope ───────────────────────────────

function unwrap<T>(response: { data: ApiResponse<T> }): T {
  const envelope = response.data;
  if (!envelope.success) {
    throw new Error(envelope.error?.message || 'Unknown API error');
  }
  return envelope.data as T;
}

// ─── Todo API ──────────────────────────────────────────────

export async function getTodos(params?: Record<string, string>): Promise<Todo[]> {
  const response = await _client.get(ENDPOINTS.todos.list, { params });
  return unwrap<{ items: Todo[] }>(response).items;
}

export async function createTodo(data: CreateTodoRequest): Promise<Todo> {
  const response = await _client.post(ENDPOINTS.todos.create, data);
  return unwrap<Todo>(response);
}

/** Optional scope for edits/claims of a recurring todo.
 *  - 'series' (or unset) updates the row in place — legacy behavior.
 *  - 'instance' forks: server creates a one-off carrying the edit and
 *    advances the series's due_at to the next occurrence.
 */
export type RecurringScope = 'series' | 'instance';

const SCOPE_TO_PROTO: Record<RecurringScope, string> = {
  series: 'RECURRING_SCOPE_SERIES',
  instance: 'RECURRING_SCOPE_INSTANCE',
};

export async function updateTodo(
  id: string,
  data: UpdateTodoRequest,
  scope?: RecurringScope,
): Promise<Todo> {
  const body: Record<string, unknown> = { ...data };
  if (scope) body.scope = SCOPE_TO_PROTO[scope];
  const response = await _client.patch(ENDPOINTS.todos.update(id), body);
  // Phase 2 of the offline-snooze plan switched UpdateTodo's response from
  // a bare Todo to UpdateTodoResponse {todo, advanced_series}. Web has no
  // notification-action surface and doesn't drive INSTANCE-scope offline
  // forks today — saga consumers only need the primary row — so we
  // unwrap `.todo` to preserve the existing Promise<Todo> contract.
  // advanced_series, when present (INSTANCE-scope recurring), is dropped;
  // the WS delta replays the series advance on a separate frame.
  const wrapped = unwrap<{ todo: Todo; advanced_series?: Todo }>(response);
  return wrapped.todo;
}

export async function deleteTodo(id: string): Promise<void> {
  await _client.delete(ENDPOINTS.todos.delete(id));
}

export async function scheduleTodoComplete(id: string): Promise<{ workflow_id: string }> {
  const response = await _client.post(ENDPOINTS.todos.scheduleComplete(id));
  return unwrap<{ workflow_id: string }>(response);
}

export async function cancelTodoComplete(id: string): Promise<void> {
  await _client.delete(ENDPOINTS.todos.scheduleComplete(id));
}

export async function claimTodo(
  id: string,
  newOwnerId: string,
  scope?: RecurringScope,
): Promise<Todo> {
  const body: Record<string, unknown> = { id, new_owner_id: newOwnerId };
  if (scope) body.scope = SCOPE_TO_PROTO[scope];
  const response = await _client.post(ENDPOINTS.todos.claim(id), body);
  return unwrap<Todo>(response);
}

export async function skipTodo(id: string): Promise<Todo> {
  const response = await _client.post(ENDPOINTS.todos.skip(id), { id });
  return unwrap<Todo>(response);
}

export type TodoBucket = 'today' | 'inbox' | 'tomorrow';

// Bucket-name → proto enum string. Server accepts either the symbolic
// name or the numeric value of the TodoBucket enum; we send the name
// for grep-ability and so curl repros stay readable.
const BUCKET_TO_PROTO: Record<TodoBucket, string> = {
  today: 'TODO_BUCKET_TODAY',
  inbox: 'TODO_BUCKET_INBOX',
  tomorrow: 'TODO_BUCKET_TOMORROW',
};

export async function moveTodo(id: string, bucket: TodoBucket): Promise<Todo> {
  const response = await _client.post(ENDPOINTS.todos.move(id), {
    id,
    bucket: BUCKET_TO_PROTO[bucket],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  return unwrap<Todo>(response);
}

// MarkBlockedTodoResponse on the wire is { followup, workflow_id }. Server
// reads the working-days lead time from system_config; the response carries
// the follow-up todo whose due_at reflects the applied lead time.
export interface MarkBlockedResult {
  followup: Todo;
  workflow_id: string;
  // Server-computed lead time the toast renders verbatim. The server
  // is authoritative; the client never back-derives this from due_at
  // (used to drift around the Sat/Sun snap-forward).
  working_days: number;
  // Full state delta — saga replays these locally so the store
  // mirrors the post-write state without waiting for a WS round-trip
  // or a refetch. See sagas.ts markBlockedTodoSaga.
  original?: Todo;
  completion?: TodoCompletion;
}

export async function markBlocked(id: string): Promise<MarkBlockedResult> {
  const response = await _client.post(ENDPOINTS.todos.markBlocked(id), { id });
  return unwrap<MarkBlockedResult>(response);
}

// UnblockTodoResponse on the wire is { already_unblocked }. Idempotent —
// `already_unblocked=true` means there was no blocked completion for this
// id at call time (already undone, or never blocked). Either way the UI
// can render the same success state; the flag is exposed for observability
// rather than gating user-facing behaviour.
export interface UnblockResult {
  already_unblocked: boolean;
  // Full delta carried in the response so the saga can replay every
  // server-side mutation locally (followup soft-deleted, original
  // back to pending, completion-row tombstoned). See sagas.ts
  // unblockTodoSaga.
  original?: Todo;
  followup_id?: string;
  completion_id?: string;
}

export async function unblock(id: string): Promise<UnblockResult> {
  const response = await _client.post(ENDPOINTS.todos.unblock(id), { id });
  return unwrap<UnblockResult>(response);
}

export async function getCompletions(
  params?: { limit?: number; offset?: number; q?: string },
  options?: { signal?: AbortSignal },
): Promise<{ items: TodoCompletion[]; total: number; hasMore: boolean }> {
  // Strip empty q so we don't send `?q=` for the unfiltered case.
  const wireParams: Record<string, unknown> = { ...params };
  if (!wireParams.q) delete wireParams.q;
  const response = await _client.get(ENDPOINTS.todos.completions, {
    params: wireParams,
    signal: options?.signal,
  });
  // Proto wire shape: { items, pagination: { page, limit, totalCount, hasNext } }.
  // The legacy { items, total } shape was removed in the proto migration but
  // the web client never updated, so `result.total` was always undefined and
  // `hasMore = items.length < total` evaluated to NaN-comparison false. The
  // load-more button silently stopped working. Read both fields here so
  // CompletionLog stays decoupled from the proto envelope.
  // Server uses protojson.UseProtoNames=true, so the wire is snake_case:
  // pagination.total_count, pagination.has_next. Reading camelCase keys
  // here returns undefined for both — exactly the silent failure mode
  // that hid this bug in the first place.
  const raw = unwrap<{ items?: TodoCompletion[]; pagination?: { total_count?: number | string; has_next?: boolean } }>(response);
  const items = raw.items ?? [];
  const totalRaw = raw.pagination?.total_count;
  const total = typeof totalRaw === 'string' ? parseInt(totalRaw, 10) : (totalRaw ?? items.length);
  const hasMore = raw.pagination?.has_next ?? false;
  return { items, total, hasMore };
}

export async function updateCompletionCloseReason(id: string, closeReason: string): Promise<TodoCompletion> {
  const response = await _client.patch(ENDPOINTS.todos.updateCompletion(id), { close_reason: closeReason });
  return unwrap<TodoCompletion>(response);
}

/**
 * List every tag name known to the household, sorted ascending by the
 * server. Sourced from TodoService.ListTags (GET /api/v1/tags). Used by
 * the tag editor's autocomplete. Goes through the generated todoService
 * client, which unwraps the envelope and parses the proto response; we
 * surface the bare string[] so callers don't depend on the wire shape.
 */
export async function getTags(): Promise<string[]> {
  const res = await todoService.listTags(_client, {});
  return res.tags;
}

export async function createSmartTodo(text: string): Promise<SmartTodoResult> {
  // Timezone now flows via the X-User-Timezone header (set by the request
  // interceptor above). The legacy `timezone` body field is deprecated and
  // no longer sent; the server still accepts it as a fallback for one
  // release.
  //
  // client_id is a client-authored row id the server honors verbatim and
  // idempotently: a retry of the same logical create (network blip, saga
  // replay) reuses it and the server returns the existing row instead of
  // forking a duplicate. Web is online-first so it has no offline-fallback
  // create, but the id keeps retries safe and matches the iOS contract.
  const response = await _client.post(ENDPOINTS.todos.smart, {
    text,
    client_id: crypto.randomUUID(),
  });
  return unwrap<SmartTodoResult>(response);
}

// ─── Plugin API ────────────────────────────────────────────

export async function getPlugins(): Promise<PluginInfo[]> {
  const response = await _client.get(ENDPOINTS.plugins.list);
  return unwrap<{ plugins: PluginInfo[] }>(response).plugins;
}

// Pext-style server-truth replay: enable/disable return the full
// PluginManifest (id, display_name, description, icon, version, enabled,
// todo_types, web_routes). The saga replays it into the toggle slice so
// post-toggle UI (e.g. description, version) converges on server state
// without a refetch.
export async function enablePlugin(id: string): Promise<PluginManifest> {
  const response = await _client.post(ENDPOINTS.plugins.enable(id));
  return unwrap<PluginManifest>(response);
}

export async function disablePlugin(id: string): Promise<PluginManifest> {
  const response = await _client.post(ENDPOINTS.plugins.disable(id));
  return unwrap<PluginManifest>(response);
}

// ─── Calendar API ─────────────────────────────────────────

export async function getCalendarEvents(params?: { start?: string; end?: string }): Promise<CalendarEvent[]> {
  const response = await _client.get(ENDPOINTS.calendar.events, { params });
  // Proto migration renamed the envelope field from `items` to `events`
  // (CalendarEventListResponse in idl/calendar.proto). Tolerate either shape
  // so stale server deployments don't crash the client with `undefined.filter`.
  const body = unwrap<{ events?: CalendarEvent[]; items?: CalendarEvent[] }>(response);
  return body.events ?? body.items ?? [];
}

export async function syncCalendar(): Promise<void> {
  await _client.post(ENDPOINTS.calendar.sync);
}

// ─── Calendar Imports (connections) ───────────────────────

export async function listCalendarConnections(): Promise<CalendarConnection[]> {
  const resp: CalendarConnectionListResponse = await calendarService.listCalendarConnections(_client, {});
  return resp.connections ?? [];
}

export async function appleCalendarSetup(email: string, appSpecificPassword: string): Promise<AppleCalendarSetupResponse> {
  return calendarService.appleCalendarSetup(_client, {
    provider: 'apple',
    email,
    app_specific_password: appSpecificPassword,
  });
}

export async function appleCalendarConnect(args: {
  email: string;
  appSpecificPassword: string;
  selectedCalendarUris: string[];
}): Promise<AppleCalendarConnectionResponse> {
  return calendarService.appleCalendarConnection(_client, {
    provider: 'apple',
    email: args.email,
    app_specific_password: args.appSpecificPassword,
    calendar_uri: args.selectedCalendarUris[0] ?? '',
    direction: 'import',
    selected_calendar_uris: args.selectedCalendarUris,
  });
}

export async function appleCalendarSync(connectionId: string): Promise<AppleCalendarSyncResponse> {
  return calendarService.appleCalendarManualSync(_client, { id: connectionId });
}

export async function appleCalendarDisconnect(connectionId: string): Promise<void> {
  await calendarService.appleCalendarDisconnect(_client, { id: connectionId });
}

export async function googleCalendarAuthURL(redirectURL: string): Promise<GoogleAuthInitResponse> {
  return calendarService.googleCalendarAuth(_client, { redirect_url: redirectURL });
}

export async function googleCalendarComplete(code: string, state: string): Promise<void> {
  await calendarService.googleCalendarCallback(_client, { code, state });
}

export async function googleCalendarDisconnect(): Promise<void> {
  await calendarService.googleCalendarDisconnect(_client, {});
}

export async function googleCalendarSync(): Promise<void> {
  await calendarService.googleCalendarSync(_client, {});
}

export async function listGoogleCalendars(): Promise<GoogleCalendarListResponse> {
  return calendarService.listGoogleCalendars(_client, {});
}

export async function updateConnectionSelection(id: string, selectedIds: string[]): Promise<CalendarConnection> {
  return calendarService.updateCalendarConnectionSelection(_client, { id, selected_ids: selectedIds });
}

export async function listSubcalendars(connectionID: string): Promise<Subcalendar[]> {
  const resp = await calendarService.listSubcalendars(_client, { connection_id: connectionID });
  return resp.subcalendars ?? [];
}

export async function updateSubcalendar(
  id: string,
  patch: { enabled?: boolean; color?: string; assignedOwnerId?: string },
): Promise<Subcalendar> {
  // The proto fields are optional — only send the keys the caller
  // actually wants to change. Empty-string values for color and
  // assignedOwnerId mean "clear the override".
  const req: any = { id };
  if (patch.enabled !== undefined) req.enabled = patch.enabled;
  if (patch.color !== undefined) req.color = patch.color;
  if (patch.assignedOwnerId !== undefined) req.assigned_owner_id = patch.assignedOwnerId;
  return calendarService.updateSubcalendar(_client, req);
}

// ─── Notifications API ────────────────────────────────────

export async function getNotifications(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Notification>> {
  const response = await _client.get(ENDPOINTS.notifications.list, { params });
  return unwrap<PaginatedResponse<Notification>>(response);
}

export async function dismissNotification(id: string): Promise<void> {
  await _client.post(ENDPOINTS.notifications.dismiss(id));
}

// ─── Household API ────────────────────────────────────────

export async function getHousehold(): Promise<Household> {
  const response = await _client.get(ENDPOINTS.household.get);
  return unwrap<Household>(response);
}

// ─── Maintenance API ─────────────────────────────────────

export async function getAppliances(): Promise<Appliance[]> {
  const response = await _client.get(ENDPOINTS.maintenance.appliances);
  return unwrap<{ items: Appliance[] }>(response).items;
}

export async function getServiceRecords(applianceId: string): Promise<ServiceRecord[]> {
  const response = await _client.get(ENDPOINTS.maintenance.serviceRecords(applianceId));
  return unwrap<{ items: ServiceRecord[] }>(response).items;
}

/**
 * Editable appliance fields shared by create and update. Server-owned
 * metadata (id, household_id, owner_id, timestamps) is never part of the
 * input — it's assigned by the backend and returned on the response.
 */
export interface ApplianceInput {
  name: string;
  category?: string;
  manufacturer?: string;
  model_number?: string;
  location?: string;
  install_date?: string;
  warranty_until?: string;
  notes?: string;
}

// The generated maintenanceService client decodes the proto envelope and
// returns the canonical Appliance message. Its shape is a structural
// superset of the domain Appliance view-model (same field names, wider
// optionals), so the boundary cast is sound — mirrors how getTodos treats
// wire JSON as the domain Todo.
export async function getAppliance(id: string): Promise<Appliance> {
  const appliance = await maintenanceService.getAppliance(_client, { id });
  return appliance as Appliance;
}

export async function createAppliance(input: ApplianceInput): Promise<Appliance> {
  const appliance = await maintenanceService.createAppliance(_client, input);
  return appliance as Appliance;
}

export async function updateAppliance(id: string, input: ApplianceInput): Promise<Appliance> {
  const appliance = await maintenanceService.updateAppliance(_client, { id, ...input });
  return appliance as Appliance;
}

export async function deleteAppliance(id: string): Promise<void> {
  await maintenanceService.deleteAppliance(_client, { id });
}

/**
 * Todos linked to an appliance via context_blob.appliance_id. The generated
 * client returns proto Todo messages; the domain Todo is a UI-refined view of
 * the same wire shape, so the boundary cast matches getTodos' handling of
 * todo JSON.
 */
export async function getApplianceTodos(applianceId: string): Promise<Todo[]> {
  const res = await maintenanceService.listApplianceTodos(_client, { id: applianceId });
  return (res.todos ?? []) as unknown as Todo[];
}

// ─── Maintenance Research API ─────────────────────────────
// The research flow: StartApplianceResearch dispatches a Temporal workflow and
// returns the run id; GetApplianceResearch is polled until status settles;
// ApplyApplianceResearch materializes operator-reviewed proposals as recurring
// todos. Types come straight from the generated contracts.

export type { ResearchResult, ResearchSource, ProposedTask, StartResearchResponse, ApplyResearchResponse };

export async function startApplianceResearch(applianceId: string): Promise<StartResearchResponse> {
  return maintenanceService.startApplianceResearch(_client, { id: applianceId });
}

export async function getApplianceResearch(applianceId: string): Promise<ResearchResult> {
  return maintenanceService.getApplianceResearch(_client, { id: applianceId });
}

export async function applyApplianceResearch(
  applianceId: string,
  researchId: string,
  tasks: ProposedTask[],
): Promise<ApplyResearchResponse> {
  return maintenanceService.applyApplianceResearch(_client, {
    id: applianceId,
    research_id: researchId,
    tasks,
  });
}

/**
 * Absolute, browser-navigable URL for a stored research document, served by
 * the raw doc-proxy route (storj is LAN-only, so the browser can't fetch it
 * directly). Honors the API base path (REACT_APP_API_URL) the same way axios
 * does, de-duping a leading /api/v1 so the result is a single, correct path
 * whether the base is "" (dev proxy) or "/api/v1" (prod).
 */
export function researchDocumentUrl(sha: string): string {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const path = `/api/v1/maintenance/research-documents/${encodeURIComponent(sha)}`;
  if (base && path.startsWith(base + '/')) {
    // base already supplies the prefix (e.g. base="/api/v1") — avoid /api/v1/api/v1.
    return path;
  }
  return base + path;
}

// ─── Projects API ─────────────────────────────────────────

export async function getProjects(): Promise<Project[]> {
  const response = await _client.get(ENDPOINTS.projects.list);
  // Proto migration: server now wraps in `{projects: [...]}` (ProjectListResponse).
  // Legacy returned a bare array. Tolerate both.
  const body = unwrap<Project[] | { projects?: Project[] }>(response);
  if (Array.isArray(body)) return body;
  return body.projects ?? [];
}

export async function createProject(data: { title: string; description?: string }): Promise<Project> {
  const response = await _client.post(ENDPOINTS.projects.create, data);
  return unwrap<Project>(response);
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const response = await _client.get(ENDPOINTS.projects.get(id));
  return unwrap<ProjectDetail>(response);
}

export async function getProjectOutlines(): Promise<ProjectDetail[]> {
  const response = await _client.get(ENDPOINTS.projects.outlines);
  // Server wraps in ProjectOutlinesResponse `{ outlines: [...] }` (proto).
  // Tolerate a bare array too, mirroring getProjects.
  const body = unwrap<ProjectDetail[] | { outlines?: ProjectDetail[] }>(response);
  if (Array.isArray(body)) return body;
  return body.outlines ?? [];
}

export async function updateProject(id: string, data: Record<string, unknown>): Promise<Project> {
  const response = await _client.put(ENDPOINTS.projects.update(id), data);
  return unwrap<Project>(response);
}

export async function deleteProject(id: string): Promise<void> {
  await _client.delete(ENDPOINTS.projects.delete(id));
}

export async function archiveProject(id: string): Promise<Project> {
  const response = await _client.post(ENDPOINTS.projects.archive(id), {});
  return unwrap<Project>(response);
}

export async function unarchiveProject(id: string): Promise<Project> {
  const response = await _client.post(ENDPOINTS.projects.unarchive(id), {});
  return unwrap<Project>(response);
}

export async function createTaskInProject(projectId: string, title: string): Promise<Todo> {
  const response = await _client.post(ENDPOINTS.projects.createTask(projectId), { title });
  return unwrap<Todo>(response);
}

export async function updateTodoPosition(
  projectId: string,
  todoId: string,
  parentId: string | null,
  sortOrder: number,
): Promise<ProjectTodoNode> {
  const path = `/api/v1/projects/${projectId}/todos/${todoId}/position`;
  const response = await _client.patch(path, {
    project_id: projectId,
    todo_id: todoId,
    parent_todo_id: parentId ?? '',
    sort_order: sortOrder,
  });
  // Server returns the canonical ProjectTodoNode after any gap-rebalance —
  // saga replays sort_order/parent_todo_id into the projects detail slice
  // so the optimistic state converges on server truth.
  return unwrap<ProjectTodoNode>(response);
}

export async function addProjectDep(projectId: string, blockedTodoId: string, blockingTodoId: string): Promise<ProjectDependency> {
  const response = await _client.post(ENDPOINTS.projects.addDep(projectId), {
    blocked_todo_id: blockedTodoId,
    blocking_todo_id: blockingTodoId,
  });
  return unwrap<ProjectDependency>(response);
}

export async function removeProjectDep(projectId: string, depId: string): Promise<void> {
  await _client.delete(ENDPOINTS.projects.removeDep(projectId, depId));
}

// ─── Contacts API ────────────────────────────────────────

export async function searchContacts(query: string): Promise<Contact[]> {
  const response = await _client.get(ENDPOINTS.contacts.search, { params: { q: query } });
  // Proto migration: server now wraps in `{results: [...]}` (ContactSearchResponse).
  const body = unwrap<Contact[] | { results?: Contact[] }>(response);
  if (Array.isArray(body)) return body;
  return body.results ?? [];
}

export async function getEnabledContacts(): Promise<Contact[]> {
  const response = await _client.get(ENDPOINTS.contacts.enabled);
  // Proto migration: server now wraps in `{contacts: [...]}` (ContactListResponse).
  const body = unwrap<Contact[] | { contacts?: Contact[] }>(response);
  if (Array.isArray(body)) return body;
  return body.contacts ?? [];
}

export async function getContact(id: string): Promise<Contact> {
  const response = await _client.get(ENDPOINTS.contacts.get(id));
  return unwrap<Contact>(response);
}

export async function createContact(data: { name: string; phone?: string; email?: string; role?: string }): Promise<Contact> {
  const response = await _client.post(ENDPOINTS.contacts.create, data);
  return unwrap<Contact>(response);
}

export async function updateContact(id: string, data: Partial<{ display_name: string; first_name: string; last_name: string; organization: string; notes: string; website: string; role: string; primary_phone: string; primary_email: string }>): Promise<Contact> {
  const response = await _client.patch(ENDPOINTS.contacts.update(id), data);
  return unwrap<Contact>(response);
}

export async function enableContact(id: string, role?: string): Promise<Contact> {
  const response = await _client.post(ENDPOINTS.contacts.enable(id), role ? { role } : undefined);
  return unwrap<Contact>(response);
}

export async function disableContact(id: string): Promise<Contact> {
  const response = await _client.post(ENDPOINTS.contacts.disable(id));
  return unwrap<Contact>(response);
}

export async function enhanceContact(id: string): Promise<{ workflow_id: string }> {
  const response = await _client.post(ENDPOINTS.contacts.enhance(id));
  return unwrap<{ workflow_id: string }>(response);
}

export async function updateContactRole(id: string, role: string): Promise<Contact> {
  const response = await _client.patch(ENDPOINTS.contacts.updateRole(id), { role });
  return unwrap<Contact>(response);
}

// ─── Background Tasks API ────────────────────────────────

export async function listTasks(params?: { status?: string; task_type?: string; workflow_id?: string; limit?: number; before?: string }): Promise<BackgroundTask[]> {
  const response = await _client.get(ENDPOINTS.tasks.list, { params });
  return unwrap<{ tasks: BackgroundTask[] }>(response).tasks;
}

export async function getTask(id: string): Promise<BackgroundTask> {
  const response = await _client.get(ENDPOINTS.tasks.get(id));
  return unwrap<BackgroundTask>(response);
}

export async function cancelTask(id: string): Promise<void> {
  await _client.post(ENDPOINTS.tasks.cancel(id));
}

// ─── Suggestions API ─────────────────────────────────────

export async function listContactSuggestions(contactId: string, status?: string): Promise<Suggestion[]> {
  const response = await _client.get(ENDPOINTS.contacts.suggestions(contactId), { params: status ? { status } : undefined });
  return unwrap<{ suggestions: Suggestion[] }>(response).suggestions;
}

export async function getSuggestion(id: string): Promise<Suggestion> {
  const response = await _client.get(ENDPOINTS.suggestions.get(id));
  return unwrap<Suggestion>(response);
}

export async function applySuggestion(id: string, selectedPaths: string[]): Promise<void> {
  await _client.post(ENDPOINTS.suggestions.apply(id), { selected_paths: selectedPaths });
}

export async function rejectSuggestion(id: string): Promise<void> {
  await _client.post(ENDPOINTS.suggestions.reject(id));
}

// ─── Admin System Info API ────────────────────────────────

import type { SystemInfoApiResponse } from '@/features/admin-system-info/types';

export async function fetchSystemInfo(): Promise<SystemInfoApiResponse> {
  const response = await _client.get(ENDPOINTS.admin.systemInfo);
  return unwrap<SystemInfoApiResponse>(response);
}

// ─── User Management API (admin only) ────────────────────

export interface ManagedUser {
  id: string;
  email: string;
  display_name: string;
  role?: string;
  created_at: string;
}

export async function listUsers(): Promise<ManagedUser[]> {
  const response = await _client.get('/users');
  // Proto migration renamed envelope `items` → `users` (UserListResponse in
  // idl/user.proto). Tolerate both for safety against staged rollouts.
  const body = unwrap<{ users?: ManagedUser[]; items?: ManagedUser[] }>(response);
  return body.users ?? body.items ?? [];
}

export async function createMember(data: { email: string; password: string; display_name: string }): Promise<ManagedUser> {
  const response = await _client.post('/users', data);
  return unwrap<ManagedUser>(response);
}

export async function updateUserRole(id: string, role: string): Promise<ManagedUser> {
  const response = await _client.patch(`/users/${id}`, { role });
  return unwrap<ManagedUser>(response);
}

export async function deleteUser(id: string): Promise<void> {
  await _client.delete(`/users/${id}`);
}

// ─── Generic HTTP Methods ────────────────────────────────

export async function post<T = any>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await _client.post(path, data, config);
  return unwrap<T>(response);
}

export async function get<T = any>(path: string, params?: Record<string, unknown>): Promise<T> {
  const response = await _client.get(path, { params });
  return unwrap<T>(response);
}

export async function patch<T = any>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await _client.patch(path, data, config);
  return unwrap<T>(response);
}

// ─── Export client for direct use ──────────────────────────

export { apiClient };
export default apiClient;
